import React from 'react';
import { useDnD } from '../../context/DnDContext';
import { TimerIcon, FileIcon } from '@radix-ui/react-icons';
import { TbMultiplier2X, TbArrowsJoin, TbArrowsSplit2 } from 'react-icons/tb';
import { BiArrowToTop } from 'react-icons/bi';
import { PiMinusSquareBold, PiSolarPanelDuotone } from 'react-icons/pi';
import { PiColumnsPlusLeft } from 'react-icons/pi';
import { ImPowerCord } from 'react-icons/im';
import { MdOutlineWindPower } from 'react-icons/md';
import { GiBanknote, GiMoneyStack } from 'react-icons/gi';
import { BsGraphUpArrow, BsBank, BsBatteryCharging } from "react-icons/bs";
import { FaArrowTrendDown } from "react-icons/fa6";
import { FaTruck } from "react-icons/fa";
import './NodeManager.css'; // Import the CSS file

const NodeManager = () => {
  const [, setType] = useDnD();

  const onDragStart = (event, nodeType) => {
    setType(nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <aside className="node-manager">
      <div className="node-manager-header">
        <p className="node-manager-kicker">Toolbox</p>
        <h2>Node Manager</h2>
        <p className="node-manager-subtitle">Drag a node into the canvas to build your pipeline.</p>
      </div>
      {/* Wind Generation - Moved to top */}
      <div
        className="node-item"
        style={{backgroundColor:'#e0f2fe',border: '1px solid #bae6fd'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'windgeneration')}
      >
        <div className="icon-container" style={{ backgroundColor: '#38bdf8' }}>
          <MdOutlineWindPower className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Wind Generation</h4>
          <p className="description">Load wind generation data</p>
        </div>
      </div>
      
      {/* Solar Generation - Moved to top */}
      <div
        className="node-item"
        style={{backgroundColor:'#fef3c7',border: '1px solid #fde68a'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'solargeneration')}
      >
        <div className="icon-container" style={{ backgroundColor: '#fbbf24' }}>
          <PiSolarPanelDuotone className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Solar Generation</h4>
          <p className="description">Load solar generation data</p>
        </div>
      </div>
      
      {/* Consumption */}
      <div
        className="node-item"
        style={{backgroundColor:'#f5f3ff',border: '1px solid #ddd6fe'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'consumption')}
      >
        <div className="icon-container" style={{ backgroundColor: '#9333ea' }}>
          <ImPowerCord className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Consumption</h4>
          <p className="description">Load consumption data</p>
        </div>
      </div>
      
      {/* Normalizer */}
      <div
        className="node-item"
        style={{backgroundColor:'#f3e8ff',border: '1px solid #e9d5ff'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'normalizer')}
      >
        <div className="icon-container" style={{ backgroundColor: '#8b5cf6' }}>
          <TimerIcon className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Normalizer</h4>
          <p className="description">Normalize time intervals</p>
        </div>
      </div>
      
      {/* Scaler */}
      <div
        className="node-item"
        style={{backgroundColor:'#ecfdf5',border: '1px solid #a7f3d0'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'scaler')}
      >
        <div className="icon-container" style={{ backgroundColor: '#10b981' }}>
          <TbMultiplier2X className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Scaler</h4>
          <p className="description">Scale data by a factor</p>
        </div>
      </div>
      
      {/* Capper */}
      <div
        className="node-item"
        style={{backgroundColor:'#eef2ff',border: '1px solid #c7d2fe'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'capper')}
      >
        <div className="icon-container" style={{ backgroundColor: '#6366f1' }}>
          <BiArrowToTop className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Capper</h4>
          <p className="description">Cap data at a limit</p>
        </div>
      </div>
      
      {/* Loss Subtractor */}
      <div
        className="node-item"
        style={{backgroundColor:'#fdf2f8',border: '1px solid #fbcfe8'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'loss')}
      >
        <div className="icon-container" style={{ backgroundColor: '#ec4899' }}>
          <PiMinusSquareBold className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Loss Subtractor</h4>
          <p className="description">Subtract loss percentage</p>
        </div>
      </div>
      
      {/* Adder */}
      <div
        className="node-item"
        style={{backgroundColor:'#e0e7ff',border: '1px solid #c7d2fe'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'adder')}
      >
        <div className="icon-container" style={{ backgroundColor: '#4f46e5' }}>
          <PiColumnsPlusLeft className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Adder</h4>
          <p className="description">Add two columns together</p>
        </div>
      </div>
      
      {/* Matcher */}
      <div
        className="node-item"
        style={{backgroundColor:'#ffedd5',border: '1px solid #fed7aa'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'matcher')}
      >
        <div className="icon-container" style={{ backgroundColor: '#ea580c' }}>
          <TbArrowsJoin className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Matcher</h4>
          <p className="description">Match consumption and generation</p>
        </div>
      </div>
      
      {/* Settle Node (priority source settlement) */}
      <div
        className="node-item"
        style={{backgroundColor:'#ede9fe',border: '1px solid #ddd6fe'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'settle')}
      >
        <div className="icon-container" style={{ backgroundColor: '#7c3aed' }}>
          <TbArrowsSplit2 className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Settle</h4>
          <p className="description">Settle an existing source in priority order</p>
        </div>
      </div>

      {/* Extrapolation Node */}
      <div
        className="node-item"
        style={{backgroundColor:'#dcfce7',border: '1px solid #86efac'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'extrapolation')}
      >
        <div className="icon-container" style={{ backgroundColor: '#22c55e' }}>
          <BsGraphUpArrow className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Extrapolation</h4>
          <p className="description">Extrapolate data for future years</p>
        </div>
      </div>
      
      {/* Banking Node */}
      <div
        className="node-item"
        style={{backgroundColor:'#d1fae5',border: '1px solid #a7f3d0'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'banking')}
      >
        <div className="icon-container" style={{ backgroundColor: '#059669' }}>
          <GiBanknote className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Banking</h4>
          <p className="description">Apply banking operations</p>
        </div>
      </div>

      {/* Battery Node */}
      <div
        className="node-item"
        style={{backgroundColor:'#ccfbf1',border: '1px solid #99f6e4'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'battery')}
      >
        <div className="icon-container" style={{ backgroundColor: '#0f766e' }}>
          <BsBatteryCharging className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Battery</h4>
          <p className="description">Charge off-peak and discharge peak</p>
        </div>
      </div>
      
      {/* Finance Node */}
      <div
        className="node-item"
        style={{backgroundColor:'#fce7f3',border: '1px solid #fbcfe8'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'finance')}
      >
        <div className="icon-container" style={{ backgroundColor: '#ec4899' }}>
          <GiMoneyStack className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Finance</h4>
          <p className="description">Apply financial calculations</p>
        </div>
      </div>
      
      {/* Loan Node */}
      <div
        className="node-item"
        style={{backgroundColor:'#eef2ff',border: '1px solid #c7d2fe'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'loan')}
      >
        <div className="icon-container" style={{ backgroundColor: '#6366f1' }}>
          <BsBank className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Loan</h4>
          <p className="description">Calculate loan amortization schedule</p>
        </div>
      </div>
      
      {/* Depreciation Node */}
      <div
        className="node-item"
        style={{backgroundColor:'#fef2f2',border: '1px solid #fecaca'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'depreciation')}
      >
        <div className="icon-container" style={{ backgroundColor: '#dc2626' }}>
          <FaArrowTrendDown className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Depreciation</h4>
          <p className="description">Calculate depreciation and tax benefits</p>
        </div>
      </div>
      
      {/* Operational Costs Node */}
      <div
        className="node-item"
        style={{backgroundColor:'#f0f9ff',border: '1px solid #bae6fd'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'operationalcosts')}
      >
        <div className="icon-container" style={{ backgroundColor: '#0ea5e9' }}>
          <FaTruck className="icon" />
        </div>
        <div className="text-container">
          <h4 className="title">Operational Costs</h4>
          <p className="description">Calculate operational costs and expenses</p>
        </div>
      </div>
      
      {/* Input Node - Moved to bottom */}
      <div
        className="node-item"
        style={{backgroundColor:'#fef3c7',border: '1px solid #fde68a'}}
        draggable
        onDragStart={(event) => onDragStart(event, 'input')}
      >
        <div className="icon-container" style={{ backgroundColor: '#fbbf24' }}>
          <FileIcon className="icon" />
        </div>
        <div className="text-container" >
          <h4 className="title">Default Input Node (depricated)</h4>
          <p className="description">Provides input data</p>
        </div>
      </div>
    </aside>
  );
};

export default NodeManager;
